import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SPREADSHEET_MIME_TYPE} from 'bulk-import/SpreadsheetBulkImportConstants';
import {BatchSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FileChooser from 'ui-library/FileChooser';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import * as WorkflowDAO from 'workflows/dao/WorkflowDAO';

type Samples = Array<{
  batchSummary: BatchSummary | null;
  entitySummary: BioentitySummary | null;
}>;

export function _getInputsFoundText(importedSamples: Samples): string {
  const batchCount = importedSamples.filter((sample) => sample.batchSummary != null).length;
  const entityCount = importedSamples.filter((sample) => sample.entitySummary != null).length;
  if (batchCount === 0 && entityCount === 0) {
    return 'No valid batches or entities found.';
  }

  assert(batchCount === 0 || entityCount === 0, 'Either batches or entities are expected, but not both.');
  if (batchCount > 0) {
    const batchOrBatches = batchCount === 1 ? 'batch' : 'batches';
    return `${batchCount} ${batchOrBatches} found.`;
  }

  const entityOrEntities = entityCount === 1 ? 'entity' : 'entities';
  return `${entityCount} ${entityOrEntities} found.`;
}

interface BulkAddWorkflowInputsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (samples: Samples) => void;
}

interface State {
  isUploading: boolean;
  importedSamples: Samples | null;
}

class BulkAddWorkflowInputsModal extends React.Component<BulkAddWorkflowInputsModalProps, State> {
  override state: State = {
    isUploading: false,
    importedSamples: null,
  };

  _isMounted = false;

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  _handleSubmit = (): void => {
    this.props.onRequestSubmit(this.state.importedSamples!);
  };

  _getButtons(): Array<ModalButton> {
    return [
      {
        key: 'add',
        onClick: this._handleSubmit,
        style: ButtonStyle.PRIMARY,
        disabled: !this.state.importedSamples || this.state.importedSamples.length === 0,
        submit: true,
        text: 'Add to workflow',
      },
    ];
  }

  _handleSelectFile = async (file: File): Promise<void> => {
    this.setState({isUploading: true});
    try {
      const spreadsheetUrl: string = (await ImportUtil.upload(file))!;
      const {samples} = await WorkflowDAO.parseWorkflowInputSpreadsheet(spreadsheetUrl, file.name);
      if (this._isMounted && samples) {
        this.setState({importedSamples: samples});
      }
    } finally {
      if (this._isMounted) {
        this.setState({isUploading: false});
      }
    }
  };

  _renderInputsFoundText(): JSX.Element {
    return <span>{_getInputsFoundText(this.state.importedSamples!)}</span>;
  }

  override render(): JSX.Element {
    return (
      <Modal
        className="bulkAddWorkflowInputsModal"
        title="Add input samples from spreadsheet"
        onRequestClose={this.props.onRequestClose}
        buttons={this._getButtons()}
      >
        {(() => {
          if (this.state.isUploading) {
            return (
              <div className="text-center u-padding-top u-padding-bottom">
                <HorizontalThrobber />
              </div>
            );
          } else if (this.state.importedSamples) {
            return this._renderInputsFoundText();
          } else {
            return (
              <div>
                <span>
                  Upload a spreadsheet of sample names with a single column called{' '}
                  {BenchlingConfig.ENABLE_BATCHES ? "either 'Batch' or " : ''}'Entity'.
                </span>
                <FileChooser
                  accept={SPREADSHEET_MIME_TYPE}
                  multiple={false}
                  onSelectFiles={this._handleSelectFile}
                />
              </div>
            );
          }
        })()}
      </Modal>
    );
  }
}

export default BulkAddWorkflowInputsModal;
